<template>
  <!-- Login page -->
  <div class="login-wrapper">

    <!-- Background decorative elements -->
    <div class="bg-orb bg-orb--1"></div>
    <div class="bg-orb bg-orb--2"></div>
    <div class="bg-orb bg-orb--3"></div>

    <!-- Heartbeat pulse rings -->
    <div class="pulse-ring pulse-ring--1"></div>
    <div class="pulse-ring pulse-ring--2"></div>

    <!-- ECG heartbeat line -->
    <svg class="ecg-line" viewBox="0 0 1200 120" preserveAspectRatio="none">
      <path class="ecg-path"
        d="M0,60 L180,60 L200,60 L210,20 L220,95 L230,10 L240,80 L250,55 L270,60 L450,60 L470,60 L480,25 L490,90 L500,15 L510,85 L520,55 L540,60 L720,60 L740,60 L750,20 L760,95 L770,10 L780,80 L790,55 L810,60 L990,60 L1010,60 L1020,25 L1030,90 L1040,15 L1050,85 L1060,55 L1080,60 L1200,60" />
    </svg>

    <!-- Header text on background -->
    <div class="login-header">
      <h1 class="login-title">One Province One Labor Room</h1>
      <p class="login-subtitle">ห้องคลอดหนึ่งเดียว (OneLR) - เขตสุขภาพที่ 1</p>
    </div>

    <!-- Glass card -->
    <div class="login-card">
      <!-- Logo -->
      <div class="login-card__logo">
        <img src="images/onelr_new_logo.png" alt="OneLR Logo">
      </div>

      <!-- Heading -->
      <h2 class="login-card__heading">ลงชื่อเข้าใช้งาน</h2>

      <!-- Form -->
      <form class="login-form" @submit.prevent="login">
        <div class="form-group">
          <label class="form-label">เลือกจังหวัด</label>
          <div class="select-wrapper">
            <select v-model="selectedProvince" class="form-select">
              <option value="0" selected>เลือกจังหวัด</option>
              <option v-for="(province, index) in provinces" :key="index" :value="province.code">
                {{ province.name_th }}
              </option>
            </select>
            <svg class="select-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clip-rule="evenodd" />
            </svg>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">เลือกโรงพยาบาล</label>
          <div class="select-wrapper">
            <select class="form-select" v-model="selectedHospital" :disabled="selectedProvince === '0'">
              <option value="0" selected>เลือกโรงพยาบาล</option>
              <option v-for="(hospital, index) in filteredHospitals" :key="index" :value="hospital.hoscode">
                {{ hospital.hosname }}
              </option>
            </select>
            <svg class="select-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clip-rule="evenodd" />
            </svg>
          </div>
        </div>

        <button type="button" class="btn-provider-id" :class="{ 'btn-disabled': selectedHospital === '0' }"
          @click="openProviderId()">
          <i class="fas fa-id-badge"></i>
          <span>ลงชื่อเข้าใช้งานด้วย Provider ID</span>
        </button>
      </form>

      <!-- Footer link -->
      <div class="login-card__footer">
        <span>สมัคร Provider ID ของกระทรวงสาธารณสุข</span>
        <a href="https://provider.id.th" target="_blank" rel="noopener">ที่นี่</a>
      </div>
    </div>

    <!-- Version / copyright -->
    <p class="login-copyright">© 2026 OneLR — เขตสุขภาพที่ 1</p>

    <!-- Custom Notification Modal -->
    <div v-if="showLoginNotification" class="custom-modal-overlay">
      <div class="custom-modal">
        <div class="custom-modal__icon">
          <i class="fas fa-bullhorn"></i>
        </div>
        <h3 class="custom-modal__title">แจ้งเปลี่ยนแปลงระบบ Login</h3>
        <div class="custom-modal__content">
          <p>ตั้งแต่วันที่ <strong>17 ก.พ. 69</strong> เป็นต้นไป</p>
          <p>ระบบได้ปรับมาใช้งาน <strong>Provider ID ของ MOPH</strong></p>
          <p class="small-note">เพื่อความสะดวกและยกระดับความปลอดภัยในการเข้าถึงข้อมูล</p>
        </div>
        <button class="custom-modal__btn" @click="closeNotification">
          รับทราบ
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";



export default {
  name: "LoginUser",
  data() {
    return {
      hospitals: [],
      provinces: [],
      selectedProvince: '0',
      selectedHospital: '0',
      showLoginNotification: false,
    };
  },
  beforeCreate() {
    localStorage.removeItem('token'); // remove the token from LocalStorage
  },
  created() {
    // [UI] สร้างตัวเลือกจังหวัด
    const url_province = process.env.VUE_APP_API_URL + '/dashboard/provinces/';
    axios.get(url_province)
      .then(response => {
        // จะได้ provinces ที่เป็น array ของ object ที่มี code และ name_th
        this.provinces = response.data
      })
      .catch(error => {
        console.log(error)
      }
      )

    // [UI] สร้างตัวเลือกโรงพยาบาล
    const url_hospitals = process.env.VUE_APP_API_URL + '/dashboard/hospitals/';
    axios.get(url_hospitals)
      .then(response => {
        this.hospitals = response.data
      })
      .catch(error => {
        console.log(error)
      }
      )
  },
  mounted() {
    // Check date to show popup notification about login change
    const today = new Date();
    const expireDate = new Date('2026-02-28T23:59:59');

    if (today <= expireDate) {
      this.showLoginNotification = true;
    }
  },
  computed: {
    // [UI] ฟังก์ชั่นการกรองโรงพยาบาลตามจังหวัดที่เลือก
    filteredHospitals() {
      // return this.hospitals.filter(hospital => hospital.provinceCode === this.selectedProvince);
      if (this.selectedProvince === "0") {
        return [];
      }
      return this.hospitals.filter(hospital => hospital.provinceCode === this.selectedProvince);
    }
  },

  methods: {
    openProviderId() {
      if (this.selectedHospital === '0') return;

      // เก็บ hosCode ที่เลือกไว้ใน localStorage เพื่อใช้ใน callback
      localStorage.setItem('provider_hosCode', this.selectedHospital);

      const clientId = '01947f9c-c00f-709f-9869-41b953301fc8';
      const redirectUri = 'https://provider.chiangmaihealth.go.th/callback/';
      const responseType = 'code';
      const projectCode = 'onelrr1';
      const random6 = Math.random().toString(36).substring(2, 8);
      const originNoProtocol = window.location.origin.replace(/^https?:\/\//, '');
      const callbackUrl = originNoProtocol + window.location.pathname + '#/callback';
      const state = `${projectCode}|${random6}|${callbackUrl}`;

      const url = `https://moph.id.th/oauth/redirect?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=${responseType}&state=${encodeURIComponent(state)}`;

      console.log('Provider ID URL:', url);
      window.location.href = url;
    },
    closeNotification() {
      this.showLoginNotification = false;
    }
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

/* ─── Wrapper / Background ─── */
.login-wrapper {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #0a3d3d 0%, #0f4c5c 30%, #1a2a4a 70%, #162040 100%);
  font-family: 'Inter', 'Sarabun', sans-serif;
  overflow: hidden;
  box-sizing: border-box;
}

/* ─── Animated background orbs (heartbeat pulse) ─── */
.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
  pointer-events: none;
  animation: heartbeat-orb 1.6s ease-in-out infinite;
}

.bg-orb--1 {
  width: 400px;
  height: 400px;
  background: #20c997;
  top: -100px;
  left: -80px;
  animation-delay: 0s;
}

.bg-orb--2 {
  width: 300px;
  height: 300px;
  background: #4dabf7;
  bottom: -60px;
  right: -60px;
  animation-delay: 0.2s;
}

.bg-orb--3 {
  width: 250px;
  height: 250px;
  background: #38d9a9;
  top: 40%;
  left: 60%;
  animation-delay: 0.4s;
}

/* Heartbeat lub-dub rhythm for orbs */
@keyframes heartbeat-orb {
  0% {
    transform: scale(1);
    opacity: 0.12;
  }

  12% {
    transform: scale(1.08);
    opacity: 0.22;
  }

  /* lub */
  24% {
    transform: scale(1);
    opacity: 0.12;
  }

  36% {
    transform: scale(1.05);
    opacity: 0.18;
  }

  /* dub */
  48% {
    transform: scale(1);
    opacity: 0.12;
  }

  100% {
    transform: scale(1);
    opacity: 0.12;
  }

  /* rest */
}

/* ─── Pulse rings (heartbeat ripple) ─── */
.pulse-ring {
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid rgba(32, 201, 151, 0.25);
  pointer-events: none;
  animation: pulse-expand 3.2s ease-out infinite;
}

.pulse-ring--1 {
  width: 80px;
  height: 80px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.pulse-ring--2 {
  width: 80px;
  height: 80px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 1.6s;
}

@keyframes pulse-expand {
  0% {
    width: 80px;
    height: 80px;
    opacity: 0.4;
    border-color: rgba(32, 201, 151, 0.35);
  }

  100% {
    width: 600px;
    height: 600px;
    opacity: 0;
    border-color: rgba(32, 201, 151, 0);
  }
}

/* ─── ECG heartbeat line ─── */
.ecg-line {
  position: absolute;
  bottom: 12%;
  left: 0;
  width: 100%;
  height: 80px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.15;
}

.ecg-path {
  fill: none;
  stroke: #20c997;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 2400;
  stroke-dashoffset: 2400;
  animation: ecg-draw 3.2s linear infinite;
}

@keyframes ecg-draw {
  0% {
    stroke-dashoffset: 2400;
    opacity: 0.6;
  }

  80% {
    stroke-dashoffset: 0;
    opacity: 0.6;
  }

  100% {
    stroke-dashoffset: 0;
    opacity: 0;
  }
}

/* ─── Header (title on background) ─── */
.login-header {
  text-align: center;
  margin-bottom: 1.5rem;
  z-index: 1;
}

.login-title {
  color: #ffffff;
  font-size: 1.35rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin: 0 0 0.25rem;
}

.login-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  font-weight: 300;
  margin: 0;
}

/* ─── Glass Card ─── */
.login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 2rem 1.5rem;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.18),
    0 1.5px 4px rgba(0, 0, 0, 0.08);
}

/* Logo */
.login-card__logo {
  text-align: center;
  margin-bottom: 1rem;
}

.login-card__logo img {
  width: 120px;
  height: auto;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.12));
}

/* Heading */
.login-card__heading {
  text-align: center;
  font-size: 1.15rem;
  font-weight: 600;
  color: #1a2a4a;
  margin: 0 0 1.5rem;
}

/* ─── Form ─── */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #475569;
  padding-left: 2px;
}

.select-wrapper {
  position: relative;
}

.form-select {
  width: 100%;
  padding: 0.7rem 2.5rem 0.7rem 0.9rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: #334155;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.form-select:focus {
  border-color: #20c997;
  box-shadow: 0 0 0 3px rgba(32, 201, 151, 0.15);
}

.form-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: #94a3b8;
  pointer-events: none;
}

/* ─── Provider ID Button ─── */
.btn-provider-id {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.8rem 1.2rem;
  margin-top: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  color: #fff;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
}

/* ─── Custom Modal ─── */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  animation: fadeIn 0.3s forwards;
}

.custom-modal {
  background: white;
  width: 90%;
  max-width: 450px;
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: scale(0.9);
  opacity: 0;
  animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  animation-delay: 0.1s;
}

.custom-modal__icon {
  width: 70px;
  height: 70px;
  background: #e6fcf5;
  color: #20c997;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin: 0 auto 1.2rem;
}

.custom-modal__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a2a4a;
  margin-bottom: 1rem;
}

.custom-modal__content {
  color: #4b5563;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.custom-modal__content p {
  margin: 0.25rem 0;
}

.small-note {
  font-size: 0.85rem;
  color: #9ca3af;
  margin-top: 0.5rem !important;
}

.custom-modal__btn {
  background: #20c997;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(32, 201, 151, 0.3);
}

.custom-modal__btn:hover {
  background: #12b886;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(32, 201, 151, 0.4);
}

.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

@keyframes popIn {
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ─── Footer link ─── */
.login-card__footer {
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.78rem;
  color: #94a3b8;
}

.login-card__footer a {
  color: #20c997;
  font-weight: 500;
  text-decoration: none;
  margin-left: 4px;
  transition: color 0.15s;
}

.login-card__footer a:hover {
  color: #12b886;
  text-decoration: underline;
}

/* ─── Copyright ─── */
.login-copyright {
  z-index: 1;
  margin-top: 1.5rem;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
}

/* ═══════════════════════════════════
   Responsive — Tablet+ (≥ 768px)
   ═══════════════════════════════════ */
@media (min-width: 768px) {
  .login-wrapper {
    padding: 3rem 2rem;
  }

  .login-title {
    font-size: 1.6rem;
  }

  .login-subtitle {
    font-size: 0.95rem;
  }

  .login-card {
    max-width: 460px;
    padding: 2.5rem 2rem;
  }

  .login-card__logo img {
    width: 140px;
  }
}

/* ═══════════════════════════════════
   Responsive — Desktop (≥ 1024px)
   ═══════════════════════════════════ */
@media (min-width: 1024px) {
  .login-title {
    font-size: 1.8rem;
  }

  .login-card {
    max-width: 480px;
    padding: 2.5rem 2.5rem;
  }

  .login-card__logo img {
    width: 160px;
  }
}
</style>